import { useMutation } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { Dispatch, SetStateAction } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import { activateDiscount } from '@/api/discount/activateDiscount';
import DiscountSVG from '@/public/page/order/discount.svg';

import { TextField } from '../Form/UI/TextField';
import Button from '../UI/Button';
import Modal from './Modal';

interface PromoCodeFormData {
  code: string;
  place: string;
}

interface PromoCodeModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  placeId: string;
}

export default function PromoCodeModal({
  open,
  setOpen,
  placeId,
}: PromoCodeModalProps) {
  const t = useTranslations('Components.Modal.PromoCodeModal');
  const activateDiscountMutation = useMutation(activateDiscount);
  const {
    control,
    handleSubmit,
    register,
    formState: { errors, isValid },
  } = useForm<PromoCodeFormData>({
    defaultValues: {
      place: placeId,
    },
  });

  const onSubmit = async (formData: PromoCodeFormData) => {
    try {
      await activateDiscountMutation
        .mutateAsync({
          payload: { code: formData.code, place: formData.place },
        })
        .then((e: any) => {
          if (e.error) {
            toast.error(e.error);
          } else {
            window.location.reload();
          }
        });
    } catch (e) {}
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      closeButton={false}
      panelClassName='backdrop-filter backdrop-blur-lg bg-[#101114]/[90]'
      padding='px-4 pb-10 mb-20'
    >
      <form className='relative' onSubmit={handleSubmit(onSubmit)}>
        <div className='pt-10'>
          <div className='isolate mx-auto flex size-14 items-center justify-center rounded-full bg-primary-filled-default'>
            <div className='absolute size-11 animate-ping-slow rounded-full bg-primary-filled-default' />
            <div className='absolute size-14 animate-ping-slow rounded-full bg-primary-filled-default' />
            <div className='absolute size-64 rounded-full bg-primary-filled-default bg-primary-filled-default/[0.12] blur-2xl' />
            <DiscountSVG className='relative z-10 h-6 w-6' />
          </div>
        </div>
        <div className='pb-5 text-center'></div>
        <div className='flex justify-center pb-5 pt-3'>
          <TextField
            {...register('code', {
              required: true,
            })}
            label={t('title')}
            placeholder={t('placeholder')}
            error={errors.code}
          />
        </div>
        <div className='flex justify-center'>
          <Button type='submit'>{t('verify')}</Button>
        </div>
      </form>
    </Modal>
  );
}
